"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import { Link } from "react-router-dom";

interface Restriction {
  id: string;
  level: string;
  title: string;
  description: string;
  rules: string[];
  active: boolean;
}

const WaterRestrictionsScreen: React.FC = () => {
  const [activeRestriction, setActiveRestriction] = useState<string>("stage1");
  
  // Sample restrictions data
  const restrictions: Restriction[] = [
    {
      id: "stage1",
      level: "Stage 1",
      title: "Moderate Water Shortage",
      description: "Conservation measures are needed to reduce water usage by 10%",
      rules: [
        "Outdoor watering limited to 2 days per week",
        "No watering between 10 AM and 6 PM",
        "No washing of driveways or sidewalks",
        "Restaurants serve water only upon request"
      ],
      active: true
    },
    {
      id: "stage2",
      level: "Stage 2",
      title: "Severe Water Shortage",
      description: "Critical conservation measures to reduce water usage by 20%",
      rules: [
        "Outdoor watering limited to 1 day per week",
        "No filling of swimming pools or fountains",
        "Car washing only at commercial facilities that recycle water",
        "No new landscape irrigation installation"
      ],
      active: false
    },
    {
      id: "stage3",
      level: "Stage 3",
      title: "Critical Water Shortage",
      description: "Emergency conservation measures to reduce water usage by 30%",
      rules: [
        "No outdoor watering whatsoever",
        "No washing of vehicles",
        "Commercial car washes closed 2 days per week",
        "Increase in water rates for excess usage"
      ],
      active: false
    },
    {
      id: "stage4",
      level: "Stage 4",
      title: "Emergency Water Shortage",
      description: "Maximum conservation measures to reduce water usage by 50%",
      rules: [
        "All outdoor water use prohibited",
        "Industrial users cut consumption by 40%",
        "Prohibition on water-based recreation",
        "Water served in restaurants only in paper cups to reduce dishwashing"
      ],
      active: false
    }
  ];
  
  // Find the active restriction
  const currentRestriction = restrictions.find(r => r.active) || restrictions[0];
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Water Restrictions
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-6 w-full">
            <div className="px-4 w-full">
              {/* Current restriction alert */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-500 rounded-full p-2 mt-1">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-amber-800">{currentRestriction.level} Restrictions Active</h2>
                    <p className="mt-1 text-sm text-amber-700">{currentRestriction.description}</p>
                    <p className="mt-3 text-xs font-medium text-amber-600">Effective until further notice</p>
                  </div>
                </div>
              </div>
              
              {/* Stage selector */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Restriction Levels</h2>
                <div className="grid grid-cols-4 gap-2">
                  {restrictions.map(restriction => (
                    <button
                      key={restriction.id}
                      onClick={() => setActiveRestriction(restriction.id)}
                      className={`py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                        activeRestriction === restriction.id 
                          ? 'bg-blue-600 text-white' 
                          : restriction.active 
                            ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}
                    >
                      {restriction.level}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Selected restriction details */}
              {restrictions.map(restriction => {
                if (restriction.id !== activeRestriction) return null;
                
                return (
                  <div key={restriction.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">{restriction.level}</h2>
                        <p className="text-sm text-gray-600 mt-1">{restriction.title}</p>
                      </div>
                      {restriction.active && (
                        <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full">
                          Active
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-4">{restriction.description}</p>
                    
                    <h3 className="text-base font-medium text-gray-900 mb-2">Key Restrictions:</h3>
                    <ul className="space-y-2">
                      {restriction.rules.map((rule, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span className="text-sm text-gray-700">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              
              {/* Conservation tips link */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mb-6">
                <h3 className="font-medium text-blue-800">Want to save water?</h3>
                <p className="text-sm text-blue-700 mt-1">View our conservation tips to help you reduce water usage during restrictions.</p>
                <Link to="/conservation-tips" className="mt-3 inline-block text-sm font-medium text-blue-600 hover:text-blue-800">
                  View Water Conservation Tips →
                </Link>
              </div>
              
              {/* Report violation */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Report a Violation</h2>
                <p className="text-sm text-gray-600 mb-4">
                  If you observe someone not following the current water restrictions, please report it to help preserve our water resources.
                </p>
                <button className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                  Report Water Restriction Violation
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
};

export default WaterRestrictionsScreen;
